import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, History, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";

export const ENTITY_LABEL = { log: "Daily Log", assignment: "Matrix Status", team: "Team", job: "Job", user: "Account" };
const ACTION_STYLE = {
  create: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  update: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  delete: "border-red-500/40 bg-red-500/10 text-red-300",
};
const ACTION_LABEL = { create: "Created", update: "Updated", delete: "Deleted" };

export const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const localDay = (iso) => {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const Val = ({ v }) => (v === undefined || v === null || v === "" ? <span className="italic text-[color:var(--muted)]">empty</span> : <span>{v}</span>);

const Changes = ({ entry }) => {
  if (!entry.changes?.length) return <span className="text-[color:var(--muted)]">-</span>;
  return (
    <ul className="space-y-1">
      {entry.changes.map((c) => (
        <li key={c.field} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="label !text-[10px]">{c.field}</span>
          {entry.action !== "create" && <span className="rounded-sm bg-red-500/10 px-1.5 py-0.5 text-red-300 line-through decoration-red-400/40"><Val v={c.before} /></span>}
          {entry.action === "update" && <ArrowRight className="h-3 w-3 text-[color:var(--muted)]" />}
          {entry.action !== "delete" && <span className="rounded-sm bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300"><Val v={c.after} /></span>}
        </li>
      ))}
    </ul>
  );
};

export const HistoryLog = ({ refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [who, setWho] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setEntries((await api.get("/audit", { params: { limit: 2000 } })).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const people = useMemo(() => [...new Map(entries.map((e) => [e.user_id, e.user_name])).entries()], [entries]);
  const rows = entries.filter((e) => {
    const day = localDay(e.at);
    return (entity === "all" || e.entity === entity) && (who === "all" || e.user_id === who) && (!from || day >= from) && (!to || day <= to);
  });
  const filtered = entity !== "all" || who !== "all" || from || to;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="history-entity-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All items</SelectItem>
            {Object.entries(ENTITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger data-testid="history-user-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All supervisors</SelectItem>
            {people.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input data-testid="history-from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
          <span className="text-[color:var(--muted)]">–</span>
          <Input data-testid="history-to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>
        {filtered && <Button variant="ghost" data-testid="history-clear-filters" onClick={() => { setEntity("all"); setWho("all"); setFrom(""); setTo(""); }}>Clear</Button>}
        <div className="ml-auto flex items-center gap-3">
          <span data-testid="history-count" className="font-mono text-xs text-[color:var(--muted)]">{rows.length} change{rows.length === 1 ? "" : "s"}</span>
          <Button variant="outline" size="sm" data-testid="history-refresh-button" onClick={load} className="border-[color:var(--border)]">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table data-testid="history-table" className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="border-b border-[color:var(--border)] text-left">
              {["When", "Who", "Action", "Item", "Changes (before → after)"].map((h) => <th key={h} className="label px-4 py-3 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} data-testid="history-empty" className="px-4 py-12 text-center text-[color:var(--muted)]">
                <History className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {filtered ? "No changes match these filters." : "No changes recorded yet. Edits made by supervisors will appear here."}
              </td></tr>
            )}
            {rows.map((e) => (
              <tr key={e.id} data-testid={`history-row-${e.id}`} className="border-b border-[color:var(--border)] align-top last:border-0 hover:bg-white/[0.02]">
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{formatDateTime(e.at)}</td>
                <td className="px-4 py-3">
                  <div className="font-display font-semibold">{e.user_name}</div>
                  <div className="font-mono text-[11px] text-[color:var(--muted)]">{e.user_email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-sm border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider ${ACTION_STYLE[e.action]}`}>{ACTION_LABEL[e.action]}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="label !text-[10px] text-amber-400">{ENTITY_LABEL[e.entity] || e.entity}</div>
                  <div className="mt-0.5">{e.summary}</div>
                </td>
                <td className="px-4 py-3"><Changes entry={e} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
